import styled from 'styled-components'

export const TransactionsContainer = styled.div`
  margin-bottom: 48px;
  margin: 32px;
`

export const SectionHeader = styled.div`
  margin-bottom: 24px;
`

export const SectionTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`

export const SectionSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`

// Filter Components
export const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`

export const FiltersPrimary = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`

export const FiltersAdditional = styled.div<{ $active: boolean }>`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  max-height: ${({ $active }) => ($active ? '200px' : '0')};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
`

export const FilterToggleBtn = styled.button<{ $expanded: boolean }>`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  font-family: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}20;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  .toggle-icon {
    transition: transform 0.3s ease;
    transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0')});
  }
`

export const FilterClearBtn = styled.button`
  padding: 8px 16px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  font-family: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

// Dropdown Filter
export const FilterDropdown = styled.div<{ $active: boolean }>`
  position: relative;
  flex: 1;
  min-width: 150px;
`

export const FilterDropdownTrigger = styled.button<{ $active: boolean }>`
  width: 100%;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border.primary};
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  box-shadow: ${({ $active, theme }) =>
    $active ? `0 0 0 3px ${theme.colors.primary}20` : 'none'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.tertiary};
    background-color: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const FilterDropdownValue = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const FilterDropdownArrow = styled.svg<{ $active: boolean }>`
  width: 12px;
  height: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: transform 0.2s;
  flex-shrink: 0;
  transform: rotate(${({ $active }) => ($active ? '180deg' : '0')});
`

export const FilterDropdownMenu = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  overflow: hidden;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  transform: translateY(${({ $active }) => ($active ? '0' : '-10px')});
  transition: all 0.2s;
  z-index: 100;
  max-height: 320px;
  overflow-y: auto;
`

export const FilterDropdownSection = styled.div`
  padding: 8px 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  }
`

export const FilterDropdownSectionTitle = styled.div`
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const FilterDropdownOption = styled.div<{ $selected: boolean }>`
  padding: 10px 16px;
  font-size: 13px;
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary : theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.15s;
  position: relative;
  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  ${({ $selected, theme }) =>
    $selected &&
    `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: ${theme.colors.primary};
    }
  `}
`

// Numeric Filter
export const FilterNumeric = styled.div<{ $active: boolean }>`
  position: relative;
  flex: 1;
  min-width: 150px;
`

export const FilterNumericTrigger = styled.button<{ $active: boolean }>`
  width: 100%;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border.primary};
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  box-shadow: ${({ $active, theme }) =>
    $active ? `0 0 0 3px ${theme.colors.primary}20` : 'none'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.tertiary};
    background-color: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const FilterNumericMenu = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  overflow: hidden;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  transform: translateY(${({ $active }) => ($active ? '0' : '-10px')});
  transition: all 0.2s;
  z-index: 100;
  padding: 20px;
  min-width: 280px;
`

export const FilterNumericHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`

export const FilterNumericInputs = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`

export const FilterNumericInputGroup = styled.div`
  flex: 1;
`

export const FilterNumericLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: 6px;
`

export const FilterNumericInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

export const FilterNumericInput = styled.input<{
  $withPrefix?: boolean
  $withSuffix?: boolean
}>`
  width: 100%;
  padding: 8px 12px;
  padding-left: ${({ $withPrefix }) => ($withPrefix ? '36px' : '12px')};
  padding-right: ${({ $withSuffix }) => ($withSuffix ? '36px' : '12px')};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  /* Hide number input spinners */
  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }
`

export const FilterNumericPrefix = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  pointer-events: none;
`

export const FilterNumericSuffix = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  pointer-events: none;
`

export const FilterNumericSeparator = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  padding-top: 20px;
`

export const FilterNumericOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`

export const FilterNumericOption = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary + '20' : theme.colors.background.tertiary};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border.primary};
  border-radius: 6px;
  font-size: 12px;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.background.primary};
    border-color: ${({ theme }) => theme.colors.text.tertiary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

export const FilterNumericActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

export const FilterNumericApply = styled.button`
  padding: 6px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}dd;
  }
`

export const FilterNumericClear = styled.button`
  padding: 6px 16px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

// Results Summary
export const ResultsSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: 6px;
`

export const ResultsCount = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`

export const ResultsSort = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const SortSelect = styled.select`
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  padding: 4px 16px 4px 4px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%2300ADE6' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right center;
  outline: none;
`

// Transaction List
export const TransactionsList = styled.div`
  margin-bottom: 24px;
`

export const TransactionItem = styled.div<{ $active?: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border.primary};
  padding: 20px 24px;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:not(:last-child) {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
    z-index: 1;
  }

  ${({ $active, theme }) =>
    $active &&
    `
    background: ${theme.colors.primary}20;
    border-color: ${theme.colors.primary};
  `}
`

export const TransactionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`

export const TransactionInfo = styled.div`
  flex: 1;
  min-width: 0;
`

export const TransactionMainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  flex-wrap: wrap;
`

export const TransactionId = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const TransactionType = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const TransactionAddress = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`

export const TransactionDetailsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex-wrap: wrap;
  margin-top: 4px;
`

export const TransactionDetailsItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const TransactionDescription = styled.div`
  font-size: 14px;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-weight: 500;
  padding-top: 4px;
  color: ${({ theme }) => theme.colors.text.primary};
`

// Transaction Badges
export const TransactionBadge = styled.span<{
  $variant: 'achat' | 'heritage' | 'vente' | 'donation'
}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  transition: all 0.2s;
  text-transform: uppercase;

  ${({ $variant }) => {
    switch ($variant) {
      case 'achat':
        return `
          background: rgba(0, 173, 230, 0.1);
          color: #0099cc;
        `
      case 'heritage':
        return `
          background: rgba(128, 90, 213, 0.1);
          color: #7b61ff;
        `
      case 'vente':
        return `
          background: rgba(0, 179, 65, 0.1);
          color: #00994d;
        `
      case 'donation':
        return `
          background: rgba(255, 149, 0, 0.1);
          color: #ff8800;
        `
      default:
        return `
          background: rgba(142, 142, 147, 0.1);
          color: #8e8e93;
        `
    }
  }}
`

export const PropertyTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  color: ${({ theme }) => theme.colors.text.secondary};
`

// Transaction Metrics
export const TransactionMetrics = styled.div`
  text-align: right;
  flex-shrink: 0;
`

export const TransactionPrice = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 2px;
  margin-top: 4px;
`

export const TransactionPricePerM2 = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4px;
`

export const TransactionArea = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const TransactionDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  white-space: nowrap;
  flex-shrink: 0;
  text-align: right;
`

// Load More
export const LoadMoreContainer = styled.div`
  text-align: center;
  margin-top: 24px;
`

export const LoadMoreBtn = styled.button`
  padding: 8px 24px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    border-color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

// Data Sources
export const DataSources = styled.div`
  margin-top: 32px;
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};

  .source-label {
    font-weight: 500;
    margin-right: 8px;
  }
`

// Mobile Styles
export const MobileStyles = styled.div`
  @media (max-width: 768px) {
    ${FiltersPrimary},
    ${FiltersAdditional} {
      flex-direction: column;
    }

    ${FilterDropdown},
    ${FilterNumeric} {
      width: 100%;
    }

    ${FilterDropdownMenu},
    ${FilterNumericMenu} {
      position: fixed;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      margin: 0;
      border-radius: 16px 16px 0 0;
      max-height: 70vh;
      transform: translateY(
        ${({ $active }: { $active: boolean }) => ($active ? '0' : '100%')}
      );
    }

    ${FilterToggleBtn},
    ${FilterClearBtn} {
      width: 100%;
      justify-content: center;
    }

    ${ResultsSummary} {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    ${TransactionHeader} {
      flex-direction: column;
      gap: 12px;
    }

    ${TransactionMetrics} {
      text-align: left;
      display: flex;
      align-items: baseline;
      gap: 16px;
    }

    ${TransactionItem} {
      padding: 16px;
    }

    ${TransactionMainRow} {
      flex-direction: column;
      align-items: flex-start;
    }

    ${TransactionDetailsRow} {
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }
  }
`
